// Resolving the pair a correlation-finder partial was crossed over: what each of the two patterns
// addresses, what it is called, and the batches they make between them. Ported from the coffee
// lib the data-lab shell used to call before handing the result down.
//
// This is the one part of the block that cannot be worked out from the payload alone -- a pattern
// is a reference into the survey and report registries, so the answers come from there.

import { getQuestionId, getSurvey, questionPositionToId } from '@aytm/app/stats/page/components/lib/utils'
import { getReportCurrentXQuestion, getReportVirtual, getTraits } from '@aytm/app/stats/lib/stat-page'

const CODEBOOK_REG = /^codebook\s*=\s*["'](.+)["']$/i
const GROUP_REG = /^(Q\d+)(A\d+)?(?:,(Q\d+)(A\d+)?)+$/i
const GROUP_PART_REG = /(Q\d+)(A\d+)?/gi
const GROUP_ID_REG = /^(Q\d+)(?:A(\d+))?$/i
// The spaces around the operator are required: optional ones leave the captured reference with a
// trailing space, and let the `in` inside a word such as "Pricing" end it.
const TAGGED_REG = /^(?:codebook|tag(?:\.sentiment)?)\s+(.+)\s+(?:=|in)\s+(?:.+)$/i
const POSITIONS_REG = /^((?:[PVCX]?Q|CL)(?:\d+))(?:E(\d+))?(?:SQ(\d+))?(?:A(\d+))?$/i

const AV_TAG_REG = /^tag(?:.sentiment)?\s*=\s*["'](.+)["']$/i
const AV_TRAIT_REG = /^(\S+)\s*=\s*["'](.+)["']$/i
const AV_TRAIT_ARRAY_REG = /^'(.+)'\s+in\s+(\S+)$/i
const AV_CODE_REG = /^tag(?:.sentiment)?\s+(Q\d+)(SQ\d+)?\s*=\s*["'](.+)["']$/i
const AV_QUESTION_REG = /^([PVCX]?Q\d+)(?:E(\d+))?(?:SQ(\d+))?(?:A(\d+))$/i
const AV_QUESTION_RO_REG = /^([PVCX]?Q\d+)(?:A(\d+))PV\s*=\s*(\d+)$/i

// Named apart from the shared `getTrait`, which takes no argument and answers the report's own.
const getTraitById = key => getTraits().find(trait => key === trait.id) || null

const surveyQuestions = () => getSurvey().questions || []

const findSurveyQuestion = id => surveyQuestions().find(question => getQuestionId(question) === id)

// A report-local question is drawn from its own chart block when it is an Xpert one, so the
// report resolves which block that currently is.
const resolveVirtual = (questions, id) =>
  'dlx' === questions[id]?.type ? getReportCurrentXQuestion(questions[id]) : questions[id]

const reportQuestions = () => getReportVirtual() || getSurvey().virtual || {}

// A polarity scale answers on three fixed points rather than on stored variants.
const answerVariantText = (question, batch, position) =>
  'psc' === question.type
    ? (1 === position && batch.text[0]) || (2 === position && batch.text[1]) || (3 === position && 'Neutral')
    : batch.answerVariants.find(av => av.position === position)?.text

export const getPositions = pattern => {
  const m = pattern.match(POSITIONS_REG)
  if (!m) return []

  return m
    .slice(1)
    .filter(value => null != value)
    .map(value => (Number.isNaN(Number(value)) ? value.toLowerCase() : Number(value)))
}

// What one pattern addresses, as [key, type, text, source].
export const getAttributesData = pattern => {
  let key = ''
  let text = ''
  let type = ''

  if (!(0 < pattern?.length)) return [key, type, text, '']

  const codebook = pattern.match(CODEBOOK_REG)
  if (codebook) return [codebook[1], codebook[1], codebook[1], 'codebook']

  if (GROUP_REG.test(pattern)) {
    const ids = Array.from(pattern.matchAll(GROUP_PART_REG), value => value[0])
    const isGroup =
      0 < ids.length &&
      ids.every(id => {
        const m = id.match(GROUP_ID_REG)
        return Boolean(findSurveyQuestion(m[1].toLowerCase()))
      })

    if (isGroup) return [pattern, pattern, pattern.toUpperCase(), 'group']

    return [key, type, text, '']
  }

  const trait = getTraitById(pattern)
  if (trait) return [pattern, pattern, trait.title, 'trait']

  const tagged = pattern.match(TAGGED_REG)
  const positions = getPositions(tagged?.[1] || pattern)
  if (!(0 < positions.length)) return [key, type, text, '']

  const questions = reportQuestions()

  let prefix
  let question = resolveVirtual(questions, positions[0])

  if (question) {
    key = question.id
    type = question.variant
    prefix = question.id.toUpperCase()
  } else {
    // both sides are lower-case ids: the positions come lower-cased, and so does the helper's answer
    question = surveyQuestions().find(q => questionPositionToId(q.position) === positions[0])
    if (question) {
      key = String(question.position)
      type = question.type
      prefix = `${(question.pre_type && 'PQ') || 'Q'}${question.visual_position}`
    }
  }

  if (question) {
    if (3 === positions.length) {
      const batch = question.batches[positions[2] - 1].text
      text = `${prefix}E${positions[1]}SQ${positions[2]}: ${question.questionVariants[positions[1] - 1].text}: ${batch}`
    }
    if (2 === positions.length) {
      const batch = question.batches[positions[1] - 1].text
      const label = ['ro', 'aro'].includes(question.type) ? 'A' : 'SQ'
      text = `${prefix}${label}${positions[1]}: ${('psc' === question.type && batch.join('-')) || batch}`
    }
    if (1 === positions.length) text = `${prefix}: ${question.text}`
  }

  return [key, type, text, 'question']
}

// A pattern crossed over a group of questions names each of them; the variants are those questions.
const groupAnswerVariants = answerVariants =>
  answerVariants
    .map((av, index) => {
      const m = av.pattern.match(GROUP_ID_REG)
      if (!m) return null

      let id = m[1].toLowerCase()
      let question = findSurveyQuestion(id)
      if (m[2]) {
        id = `${id}a${m[2]}`
        // a member names its answer by position, not by its index in the list
        question = question.answerVariants.find(av => av.position === Number(m[2]))
      }
      if (!question) return null

      return { prefix: id.toUpperCase(), text: question.text, position: index + 1 }
    })
    .filter(Boolean)

// Every other pattern names its variants one by one, each addressing a tag, a trait value, or a
// point on a question.
const patternAnswerVariants = (pattern, answerVariants) =>
  answerVariants
    .map((av, index) => {
      const position = index + 1

      const tag = av.pattern.match(AV_TAG_REG)
      if (tag) {
        const codebook = pattern.match(CODEBOOK_REG)
        return { text: tag[1].replace(`${codebook[1]}: `, ''), position }
      }

      const traitValue = av.pattern.match(AV_TRAIT_REG)
      if (traitValue) {
        const trait = getTraitById(traitValue[1])
        if (!trait) return null

        const value = trait.values.find(v => String(traitValue[2]).toLowerCase() === String(v.id).toLowerCase())
        return { text: value.name, position }
      }

      const traitArray = av.pattern.match(AV_TRAIT_ARRAY_REG)
      if (traitArray) {
        const trait = getTraitById(traitArray[2])
        if (!trait) return null

        const value = trait.values.find(v => String(traitArray[1]).toLowerCase() === String(v.id).toLowerCase())
        return { text: value.name, position }
      }

      const questions = reportQuestions()

      const code = av.pattern.match(AV_CODE_REG)
      if (code) {
        const id = code[1].toLowerCase()
        const question = resolveVirtual(questions, id) || findSurveyQuestion(id)
        if (!question) return null

        return { prefix: `A${position}`, text: code[3], position }
      }

      const point = av.pattern.match(AV_QUESTION_REG)
      if (point) {
        const id = point[1].toLowerCase()
        const question = resolveVirtual(questions, id) || findSurveyQuestion(id)
        if (!question) return null

        if (null != point[2]) {
          const batch = question.batches.find(b => b.position === Number(point[3]))
          const av_ = batch.answerVariants.find(v => v.position === Number(point[4]))
          if (!av_) return null

          return { prefix: `A${position}`, text: av_.text, position }
        }

        if (null != point[3]) {
          const batch = question.batches.find(b => b.position === Number(point[3]))
          if (!batch) return null

          const text = answerVariantText(question, batch, Number(point[4]))
          if (!text) return null

          return { prefix: `A${position}`, text, position }
        }

        const variants = ('md' === question.type && question.batches) || question.answerVariants
        const av_ = variants.find(v => v.position === Number(point[4]))
        if (!av_) return null

        return { prefix: `A${position}`, text: av_.text, position }
      }

      const ranked = av.pattern.match(AV_QUESTION_RO_REG)
      if (ranked) {
        const id = ranked[1].toLowerCase()
        const question = resolveVirtual(questions, id) || findSurveyQuestion(id)
        if (!question) return null

        const batch = question.batches.find(b => b.position === Number(ranked[2]))
        if (!batch) return null

        const text = answerVariantText(question, batch, Number(ranked[3]))
        if (!text) return null

        return { prefix: `A${position}`, text, position }
      }

      return null
    })
    .filter(Boolean)

export const getAnswerVariantsData = (pattern, answerVariants) =>
  GROUP_REG.test(pattern) ? groupAnswerVariants(answerVariants) : patternAnswerVariants(pattern, answerVariants)

// The pair a partial was crossed over. Answers null for batches that carry no pattern to resolve --
// a host that worked the pair out for itself hands the payload in ready to draw.
export const calcPartialData = (batches = []) => {
  if (2 > batches.length || !batches.slice(0, 2).every(batch => 0 < batch?.pattern?.length)) return null

  const rows = getAnswerVariantsData(batches[0].pattern, batches[0].answerVariants)
  const columns = getAnswerVariantsData(batches[1].pattern, batches[1].answerVariants)

  const rowAttributes = getAttributesData(batches[0].pattern)
  const columnAttributes = getAttributesData(batches[1].pattern)

  return {
    key: [rowAttributes[0], columnAttributes[0]],
    type: [rowAttributes[1], columnAttributes[1]],
    text: [rowAttributes[2], columnAttributes[2]],
    source: [rowAttributes[3], columnAttributes[3]],
    batches: rows.map(row => ({ ...row, answerVariants: structuredClone(columns) })),
    ignoreTotal: batches.some(
      batch =>
        ('number' === batch.patternType && 0 < batch.base?.length) ||
        batch.answerVariants.some(av => 0 < av.base?.length)
    )
  }
}
